/*****************************************************************************
 * MASCA — Popup de ubicación
 * ---------------------------------------------------------------------------
 * REDISEÑO DE UX. Qué cambió respecto de la versión anterior:
 *  1. El resultado de cobertura ya NO se muestra con alert() del navegador: se
 *     pinta dentro del modal como un tercer estado (tienda + ETA + rutas). Era
 *     el momento más importante del flujo y se veía como un error del sistema.
 *  2. El encabezado ya no se oculta al elegir dirección: cambia de texto. Antes
 *     el modal quedaba sin título, con un input huérfano y un mapa sin contexto.
 *  3. El autocomplete tiene debounce (250 ms) + AbortController: antes disparaba
 *     un fetch por tecla y las respuestas fuera de orden pintaban resultados
 *     viejos. Ahora además tiene estados de cargando / vacío / error y se navega
 *     con teclado (flechas + Enter + Escape).
 *  4. "Confirmar" nace deshabilitado hasta que hay coordenadas.
 *  5. La dirección que resuelve el pin se muestra en pantalla: es lo que el
 *     comprador está confirmando. Antes se actualizaba en silencio.
 *  6. Accesibilidad: foco al abrir, foco atrapado, cierre con Escape, scroll del
 *     body bloqueado, roles ARIA en la lista de sugerencias.
 *  7. El `location.reload()` ocurre cuando el comprador aprieta "Ver productos",
 *     no de golpe apenas confirma.
 *  8. Un error de RED al consultar cobertura ya no se guarda como "sin
 *     cobertura": se avisa y se deja reintentar. Antes una caída del backend
 *     dejaba al comprador marcado como fuera de zona.
 *
 * Lo que NO cambió: la fuente de verdad sigue siendo `MascaEstado`
 * (masca-estado.js) — TTL de 12 h, espejos legacy, reconciliación del atributo
 * de carrito en cada carga y precarga de la dirección anterior.
 *
 * PENDIENTE (no es estético): la API key de Geoapify está en el cliente. Debe
 * salir por el App Proxy (/apps/masca/geocode) — regla 7 del CLAUDE.md.
 *****************************************************************************/

/***********************
 * VARIABLES GLOBALES
 ***********************/
const popup = document.getElementById("location-popup-section");
const content = document.getElementById("location-popup-content");
const closeBtn = document.getElementById("location-popup-close");
const submitBtn = document.getElementById("location-submit");
const navegadorBtn = document.getElementById("location-navegador");
const changeLocationBtn = document.getElementById("change-location-btn");

// Nodos del rediseño
const tituloEl = document.getElementById("mloc-titulo");
const subEl = document.getElementById("mloc-sub");
const avisoEl = document.getElementById("mloc-aviso");
const direccionTextoEl = document.getElementById("mloc-direccion-texto");
const resultadoCajaEl = document.getElementById("mloc-resultado-caja");
const resultadoTituloEl = document.getElementById("mloc-resultado-titulo");
const resultadoTextoEl = document.getElementById("mloc-resultado-texto");
const chipsEl = document.getElementById("mloc-chips");
const resultadoCtaEl = document.getElementById("mloc-resultado-cta");
const resultadoCambiarEl = document.getElementById("mloc-resultado-cambiar");

const geoApifyKey = "24da6212d08f413fb105d86ae26461e9";

/**
 * Nombres que son BARRIO y no comuna. Geoapify mezcla los dos en los mismos
 * campos: para Dublé Almeyda 2020 devuelve "Ñuñoa" (comuna) y "Barrio Eusebio
 * Lillo" (barrio) uno al lado del otro, y para la misma calle también aparecen
 * "Villa Frei" y "Población Empleados Públicos y Periodistas Chile-España".
 *
 * Ninguno de esos nombres es una comuna, pero se colaban al frente de la lista
 * y el comprador leía su dirección terminada en un barrio que no reconoce.
 * No se DESCARTAN —podría haber una comuna que empiece así— sino que se mandan
 * al final: para el display gana la comuna, y para el backend el orden importa
 * porque `identificarComuna` se queda con la primera que reconoce.
 */
const PREFIJO_NO_COMUNA =
  /^(barrio|villa|poblaci[oó]n|sector|conjunto|condominio|residencial|unidad vecinal|loteo|provincia|regi[oó]n)\b/i;

/**
 * Separa los niveles administrativos de Geoapify en dos grupos, ambos ya sin
 * repetidos y ordenados del más específico al más general.
 *
 *   probables  candidatos a COMUNA.
 *   descartes  lo que seguro no lo es: barrios ("Barrio Eusebio Lillo", "Villa
 *              Frei", "Población …") y niveles por encima de la comuna
 *              ("Provincia de Santiago", "Región Metropolitana").
 *
 * El grupo existe porque Geoapify mezcla comuna y barrio en los mismos campos,
 * sin ninguna marca que los distinga: para Dublé Almeyda 2020 devuelve "Ñuñoa"
 * y "Barrio Eusebio Lillo" uno al lado del otro, y cuál viene primero cambia
 * según la dirección.
 */
function nivelesGeoapify(props) {
  const niveles = [
    props.suburb,
    props.district,
    props.city_district,
    props.municipality,
    props.city,
    // `town`/`village` aparecen en comunas chicas fuera del Gran Santiago, donde
    // Geoapify no usa `city` (p. ej. El Quisco, Algarrobo). No aportan hoy
    // —MASCA solo opera Santiago— pero cuestan cero y evitan un hueco silencioso
    // el día que se abra una zona nueva.
    props.town,
    props.village,
    props.county,
  ];
  const vistas = new Set();
  const probables = [];
  const descartes = [];
  for (const nivel of niveles) {
    if (typeof nivel !== "string") continue;
    const limpio = nivel.trim();
    if (!limpio) continue;
    const clave = limpio.toLowerCase();
    if (vistas.has(clave)) continue;
    vistas.add(clave);
    (PREFIJO_NO_COMUNA.test(limpio) ? descartes : probables).push(limpio);
  }
  return { probables, descartes };
}

/**
 * Lista de comunas candidatas que viaja al backend.
 *
 * POR QUÉ UNA LISTA Y NO UN CAMPO. Geoapify no tiene un campo fijo para la
 * comuna chilena. En el Gran Santiago `city` suele ser "Santiago" aunque la
 * dirección esté en Ñuñoa (la comuna real cae en `suburb`/`district`), pero en
 * comunas que son ciudad por sí solas —Maipú, Puente Alto— la comuna SÍ está en
 * `city`. Cualquier campo único que elijamos acá se equivoca en la mitad de los
 * casos, así que el widget no decide: manda todos los niveles y el backend, que
 * tiene la lista de comunas en `shipping_rates`, elige cuál es comuna de verdad.
 *
 * Importa: la comuna decide la ruta de BODEGA (el partner cubre por comuna, no
 * por radio). Mandar "Santiago" para una dirección de Ñuñoa no es un detalle de
 * texto, es prometer o negar una entrega equivocada.
 *
 * Van TODOS los niveles, con los probables primero: no se descarta ninguno
 * —podría existir una comuna que empiece con una de esas palabras— pero el
 * orden importa, porque `identificarComuna` se queda con la primera que
 * reconoce contra `shipping_rates`.
 */
function comunasCandidatas(props) {
  if (!props) return [];
  const { probables, descartes } = nivelesGeoapify(props);
  return probables.concat(descartes);
}

/**
 * Formatea la dirección de Geoapify de forma legible: calle número, comuna,
 * ciudad — SIN código postal, región ni "Chile" (que se veían feos, y la región
 * es siempre la misma en el mercado de MASCA). Pendiente #20.
 * Preferimos armarla desde los componentes; si faltan, limpiamos `formatted`
 * quitándole el código postal y el país.
 */
function formatoDireccionLinda(props) {
  if (!props) return "";
  const calle = [props.street, props.housenumber].filter(Boolean).join(" ");
  // Los dos niveles más específicos que PUEDEN ser comuna: en la práctica
  // comuna y ciudad. Antes se mostraba `city` + `state` y se leía "Dublé
  // Almeyda 2020, Santiago, Región Metropolitana de Santiago" para una
  // dirección de Ñuñoa: la comuna, que es el dato que el comprador reconoce,
  // no aparecía por ningún lado. Los barrios y las provincias quedan fuera del
  // texto —son ruido— aunque igual se manden al backend.
  const zona = nivelesGeoapify(props).probables.slice(0, 2);

  const partes = [calle, ...zona].filter(Boolean);
  if (partes.length > 0) {
    // Deduplica segmentos repetidos (p. ej. comuna == ciudad).
    return [...new Set(partes)].join(", ");
  }

  // Fallback: limpiar el `formatted` (le sacamos el código postal y "Chile").
  let s = props.formatted || "";
  if (props.postcode) s = s.split(props.postcode).join("");
  return s
    .replace(/,\s*Chile\s*$/i, "")
    .replace(/\s{2,}/g, " ")
    .replace(/,\s*,/g, ",")
    .replace(/^\s*,\s*/, "")
    .replace(/,\s*$/, "")
    .trim();
}

/**
 * Arma el `selectedGeoData` desde una respuesta de Geoapify. Existe para que
 * los tres caminos que eligen dirección —autocomplete, GPS y arrastre del pin—
 * guarden exactamente los mismos campos; estaba copiado y pegado tres veces, y
 * un campo que se agregara en uno solo se perdía en los otros dos.
 */
function geoDataDesde(props, lat, lng) {
  return {
    street: props.street,
    housenumber: props.housenumber,
    city: props.city,
    municipality: props.municipality,
    state: props.state,
    address: formatoDireccionLinda(props),
    comunas: comunasCandidatas(props),
    lat: lat,
    lng: lng,
  };
}

let map = null;
let marker = null;
let selectedCoords = null;
let selectedGeoData = null;
/** Input que crea `addressAutocomplete`. Lo guardamos para poder precargarlo. */
let autocompleteInput = null;
let ultimoFocoPrevio = null;

/***********************
 * ESTADOS DEL MODAL
 * "form" -> pedir dirección | "mapa" -> ajustar pin | "resultado" -> cobertura
 * El encabezado acompaña al estado en vez de desaparecer.
 ***********************/
const COPY_POR_ESTADO = {
  form: {
    titulo: "¿Dónde te lo entregamos?",
    sub: "Con tu dirección te mostramos los productos de la tienda que llega a tu zona.",
  },
  mapa: {
    titulo: "Confirma el punto exacto",
    sub: "Revisa que el pin esté en tu casa o edificio antes de continuar.",
  },
  resultado: {
    titulo: "Tu zona de entrega",
    sub: "Esto es lo que podemos llevarte a esta dirección.",
  },
};

function setEstado(estado) {
  if (!popup) return;
  popup.dataset.estado = estado;
  const copy = COPY_POR_ESTADO[estado];
  if (copy) {
    tituloEl.textContent = copy.titulo;
    subEl.textContent = copy.sub;
  }
  // El mapa se crea oculto: hay que recalcular su tamaño al mostrarlo.
  if (estado === "mapa" && map) setTimeout(() => map.invalidateSize(), 60);
}

/** Aviso contextual dentro del modal (reemplaza los alert() de bloqueo). */
function mostrarAviso(texto, tono) {
  if (!avisoEl) return;
  if (!texto) {
    avisoEl.hidden = true;
    return;
  }
  avisoEl.textContent = texto;
  avisoEl.className = "mloc-aviso mloc-aviso--" + (tono || "warn");
  avisoEl.hidden = false;
}

/***********************
 * POPUP CONTROL (+ accesibilidad)
 * La decisión de abrir el popup al cargar NO se toma acá: la toma
 * `arrancarMasca()` al final del archivo, porque necesita el autocomplete ya
 * construido para poder precargar la dirección anterior.
 ***********************/
const SELECTOR_FOCUSABLE =
  'button:not([disabled]), input, a[href], [tabindex]:not([tabindex="-1"])';

function abrirPopup() {
  if (!popup) return;
  ultimoFocoPrevio = document.activeElement;
  popup.classList.add("is-open");
  document.body.style.overflow = "hidden"; // sin doble scroll detrás del modal
  // En móvil no forzamos el teclado de inmediato: molesta más de lo que ayuda.
  if (autocompleteInput && window.matchMedia("(min-width: 750px)").matches) {
    autocompleteInput.focus();
  }
}

function cerrarPopup() {
  if (!popup) return;
  popup.classList.remove("is-open");
  document.body.style.overflow = "";
  mostrarAviso(null);
  if (ultimoFocoPrevio && typeof ultimoFocoPrevio.focus === "function") {
    ultimoFocoPrevio.focus();
  }
}

changeLocationBtn?.addEventListener("click", () => {
  openLocationPopup();
});

closeBtn.addEventListener("click", () => {
  localStorage.removeItem("pendingAddToCart");
  cerrarPopup();
});

// Click fuera del contenido cierra (las sugerencias y el GPS no cuentan).
popup.addEventListener("click", (e) => {
  if (
    !content.contains(e.target) &&
    !e.target.closest(".autocomplete-items") &&
    !e.target.closest("#location-navegador")
  ) {
    cerrarPopup();
  }
});

// Escape cierra; Tab queda atrapado dentro del modal.
document.addEventListener("keydown", (e) => {
  if (!popup.classList.contains("is-open")) return;

  if (e.key === "Escape") {
    // Si la lista de sugerencias está abierta, Escape la cierra primero.
    const lista = document.querySelector(".autocomplete-items");
    if (lista) {
      lista.remove();
      return;
    }
    cerrarPopup();
    return;
  }

  if (e.key === "Tab") {
    const focusables = [...content.querySelectorAll(SELECTOR_FOCUSABLE)].filter(
      (el) => el.offsetParent !== null
    );
    if (focusables.length === 0) return;
    const primero = focusables[0];
    const ultimo = focusables[focusables.length - 1];
    if (e.shiftKey && document.activeElement === primero) {
      e.preventDefault();
      ultimo.focus();
    } else if (!e.shiftKey && document.activeElement === ultimo) {
      e.preventDefault();
      primero.focus();
    }
  }
});

/** Global: la usa external-cart.js y el gate de add-to-cart. */
function openLocationPopup() {
  if (!popup) return;

  // Precargar la dirección anterior: quien vuelve no debería escribirla de
  // nuevo, solo confirmarla. Si ya eligió una en esta sesión, no la pisamos.
  if (!selectedGeoData && window.MascaEstado) {
    precargarUbicacionAnterior(MascaEstado.leer());
  }

  setEstado(selectedGeoData && selectedCoords ? "mapa" : "form");
  habilitarConfirmar(!!selectedGeoData);
  abrirPopup();
}

/** Global: la usa external-cart.js. */
function hasSelectedVendor() {
  const vendorId = localStorage.getItem("currentVendorId");
  return vendorId && vendorId !== "null";
}

/***********************
 * AUTOCOMPLETE
 * Con debounce, cancelación real (AbortController) y estados visibles.
 ***********************/
function addressAutocomplete(container, callback) {
  const input = document.createElement("input");
  input.type = "text";
  input.placeholder = "Calle y número, comuna";
  input.setAttribute("autocomplete", "off");
  input.setAttribute("role", "combobox");
  input.setAttribute("aria-expanded", "false");
  input.setAttribute("aria-label", "Dirección de entrega");
  container.appendChild(input);
  autocompleteInput = input;

  let debounceId = null;
  let controlador = null;
  let indiceActivo = -1;

  function cerrarLista() {
    const list = container.querySelector(".autocomplete-items");
    if (list) list.remove();
    input.setAttribute("aria-expanded", "false");
    indiceActivo = -1;
  }

  function abrirLista() {
    cerrarLista();
    const list = document.createElement("div");
    list.className = "autocomplete-items";
    list.setAttribute("role", "listbox");
    container.appendChild(list);
    input.setAttribute("aria-expanded", "true");
    return list;
  }

  function pintarMensaje(texto, conSpinner) {
    const list = abrirLista();
    const div = document.createElement("div");
    div.className = "mloc-item-vacio";
    if (conSpinner) {
      const sp = document.createElement("span");
      sp.className = "mloc-spinner mloc-spinner--inline";
      div.appendChild(sp);
    }
    div.appendChild(document.createTextNode(texto));
    list.appendChild(div);
  }

  function marcarActivo(items) {
    items.forEach((el, i) => el.classList.toggle("is-activo", i === indiceActivo));
    if (indiceActivo >= 0) items[indiceActivo].scrollIntoView({ block: "nearest" });
  }

  input.addEventListener("input", function () {
    const value = this.value.trim();
    if (debounceId) clearTimeout(debounceId);
    if (controlador) controlador.abort();

    if (value.length < 3) {
      cerrarLista();
      return;
    }

    pintarMensaje("Buscando direcciones…", true);

    debounceId = setTimeout(() => {
      controlador = new AbortController();
      fetch(
        `https://api.geoapify.com/v1/geocode/autocomplete?text=${encodeURIComponent(
          value
        )}&limit=5&lang=es&filter=countrycode:cl&apiKey=${geoApifyKey}`,
        { signal: controlador.signal }
      )
        .then((r) => r.json())
        .then((data) => {
          const features = (data && data.features) || [];
          if (features.length === 0) {
            pintarMensaje("No encontramos esa dirección. Prueba con calle y número.", false);
            return;
          }

          const list = abrirLista();
          // Geoapify devuelve varios puntos de la misma cuadra que, ya sin el
          // barrio, se leen IGUAL ("Dublé Almeyda 2020, Ñuñoa" tres veces).
          // Tres filas idénticas no ayudan a elegir: se muestra la primera.
          const yaMostradas = new Set();
          features.forEach((feature) => {
            const etiqueta = formatoDireccionLinda(feature.properties);
            if (!etiqueta || yaMostradas.has(etiqueta)) return;
            yaMostradas.add(etiqueta);

            const item = document.createElement("div");
            item.setAttribute("role", "option");
            item.textContent = etiqueta;
            item.addEventListener("click", (e) => {
              e.stopPropagation();
              input.value = etiqueta;
              cerrarLista();
              callback(feature);
            });
            list.appendChild(item);
          });

          if (yaMostradas.size === 0) {
            pintarMensaje("No encontramos esa dirección. Prueba con calle y número.", false);
          }
        })
        .catch((err) => {
          if (err && err.name === "AbortError") return; // búsqueda reemplazada
          console.error("MASCA: error en el autocomplete", err);
          pintarMensaje("No pudimos buscar ahora. Revisa tu conexión.", false);
        });
    }, 250);
  });

  // Teclado: flechas para recorrer, Enter para elegir.
  input.addEventListener("keydown", (e) => {
    const items = [...container.querySelectorAll('.autocomplete-items div[role="option"]')];
    if (items.length === 0) return;

    if (e.key === "ArrowDown") {
      e.preventDefault();
      indiceActivo = (indiceActivo + 1) % items.length;
      marcarActivo(items);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      indiceActivo = (indiceActivo - 1 + items.length) % items.length;
      marcarActivo(items);
    } else if (e.key === "Enter" && indiceActivo >= 0) {
      e.preventDefault();
      items[indiceActivo].click();
    }
  });

  document.addEventListener("click", (e) => {
    if (e.target !== input) cerrarLista();
  });
}

/***********************
 * AUTOCOMPLETE CALLBACK
 ***********************/
addressAutocomplete(document.getElementById("autocomplete-container"), (data) => {
  const props = data.properties;

  selectedCoords = { lat: props.lat, lng: props.lon };
  selectedGeoData = geoDataDesde(props, props.lat, props.lon);

  mostrarAviso(null);
  initMap(props.lat, props.lon);
  pintarDireccionResuelta(selectedGeoData.address);
  setEstado("mapa");
  habilitarConfirmar(true);
});

/***********************
 * GPS
 ***********************/
navegadorBtn.addEventListener("click", () => {
  if (!navigator.geolocation) {
    mostrarAviso("Tu navegador no permite compartir la ubicación. Escribe tu dirección.", "warn");
    return;
  }

  const textoGps = navegadorBtn.querySelector("p");
  const textoOriginal = textoGps.textContent;
  navegadorBtn.disabled = true;
  textoGps.textContent = "Buscando tu ubicación…";

  const restaurar = () => {
    navegadorBtn.disabled = false;
    textoGps.textContent = textoOriginal;
  };

  navigator.geolocation.getCurrentPosition(
    async (pos) => {
      const lat = pos.coords.latitude;
      const lng = pos.coords.longitude;
      selectedCoords = { lat, lng };

      // Pintamos el mapa de inmediato; el nombre de la calle llega después.
      mostrarAviso(null);
      initMap(lat, lng);
      setEstado("mapa");
      pintarDireccionResuelta(null); // spinner

      try {
        const res = await fetch(
          `https://api.geoapify.com/v1/geocode/reverse?lat=${lat}&lon=${lng}&lang=es&apiKey=${geoApifyKey}`
        );
        const json = await res.json();
        const props = json.features[0]?.properties;
        if (props) {
          selectedGeoData = geoDataDesde(props, lat, lng);
          if (autocompleteInput) autocompleteInput.value = selectedGeoData.address;
          pintarDireccionResuelta(selectedGeoData.address);
          habilitarConfirmar(true);
        } else {
          // Sin dirección no podemos consultar cobertura: pedimos escribirla.
          pintarDireccionResuelta("No pudimos reconocer la calle en ese punto");
          mostrarAviso("Escribe tu dirección para verificar la cobertura.", "warn");
          habilitarConfirmar(false);
        }
      } catch (err) {
        console.error("MASCA: error en reverse geocoding", err);
        pintarDireccionResuelta("No pudimos reconocer la calle en ese punto");
        mostrarAviso("Escribe tu dirección para verificar la cobertura.", "warn");
        habilitarConfirmar(false);
      }
      restaurar();
    },
    () => {
      restaurar();
      mostrarAviso(
        "No pudimos obtener tu ubicación. Revisa los permisos del navegador o escribe tu dirección.",
        "warn"
      );
    },
    { timeout: 10000 }
  );
});

/***********************
 * DIRECCIÓN RESUELTA (lo que realmente se confirma)
 ***********************/
function pintarDireccionResuelta(texto) {
  if (!direccionTextoEl) return;
  direccionTextoEl.innerHTML = "";

  if (!texto) {
    const sp = document.createElement("span");
    sp.className = "mloc-spinner mloc-spinner--inline";
    direccionTextoEl.appendChild(sp);
    direccionTextoEl.appendChild(document.createTextNode("Ubicando dirección…"));
    return;
  }

  const etiqueta = document.createElement("strong");
  etiqueta.textContent = "Entregar en: ";
  direccionTextoEl.appendChild(etiqueta);
  direccionTextoEl.appendChild(document.createTextNode(texto));
}

function habilitarConfirmar(ok) {
  submitBtn.disabled = !ok;
}

/***********************
 * MAPA
 ***********************/
function initMap(lat, lng) {
  if (!map) {
    // scrollWheelZoom off: scrollear dentro del modal no debe secuestrar el mapa.
    map = L.map("map", { scrollWheelZoom: false }).setView([lat, lng], 16);

    const icon = L.icon({
      iconUrl: `https://api.geoapify.com/v1/icon/?type=material&icon=pets&color=%23ff0000&contentColor=%23ffffff&noWhiteCircle&apiKey=${geoApifyKey}`,
      iconSize: [36, 50],
      iconAnchor: [18, 50],
    });

    L.tileLayer(
      `https://maps.geoapify.com/v1/tile/carto/{z}/{x}/{y}.png?apiKey=${geoApifyKey}`,
      { maxZoom: 20 }
    ).addTo(map);

    marker = L.marker([lat, lng], { draggable: true, icon: icon }).addTo(map);
    marker.on("dragstart", () => {
      habilitarConfirmar(false);
      pintarDireccionResuelta(null);
    });
    marker.on("dragend", onMarkerMoved);
  } else {
    map.setView([lat, lng], 16);
    marker.setLatLng([lat, lng]);
  }

  setTimeout(() => map.invalidateSize(), 200);
}

async function onMarkerMoved(e) {
  const { lat, lng } = e.target.getLatLng();
  selectedCoords = { lat, lng };

  try {
    const res = await fetch(
      `https://api.geoapify.com/v1/geocode/reverse?lat=${lat}&lon=${lng}&lang=es&apiKey=${geoApifyKey}`
    );
    const json = await res.json();
    const props = json.features[0]?.properties;

    if (props) {
      selectedGeoData = geoDataDesde(props, lat, lng);
      pintarDireccionResuelta(selectedGeoData.address);
    } else {
      // Sin calle conocida igual conservamos las coordenadas movidas.
      if (selectedGeoData) {
        selectedGeoData.lat = lat;
        selectedGeoData.lng = lng;
      }
      pintarDireccionResuelta("Ubicación marcada en el mapa");
    }
  } catch (err) {
    console.error("MASCA: error en reverse geocoding", err);
    if (selectedGeoData) {
      selectedGeoData.lat = lat;
      selectedGeoData.lng = lng;
    }
    pintarDireccionResuelta("Ubicación marcada en el mapa");
  }

  habilitarConfirmar(!!selectedGeoData);
}

/***********************
 * CONFIRMAR UBICACIÓN  (conectado a MASCA)
 * Al confirmar: consulta cobertura al backend MASCA (vía App Proxy), guarda el
 * estado único (MascaEstado) y deriva de él los atributos de carrito que lee la
 * Shopify Function del checkout.
 ***********************/
/** ¿La respuesta de cobertura permite entrega el MISMO día? */
function esExpress(cobertura) {
  return !!(
    cobertura &&
    cobertura.modalidades &&
    cobertura.modalidades.express &&
    cobertura.modalidades.express.disponible
  );
}

/**
 * Etiquetas legibles de cada ruta para los chips del resultado.
 *
 * OJO CON LAS LLAVES: son las que emite el backend en `rutas` —`express`,
 * `nextday`, `bodega`—, no las de la versión vieja (`petshop`). Con las llaves
 * viejas solo calzaba `bodega`: el día que vuelva el express, su chip no se
 * pintaba y nadie se iba a dar cuenta, porque no hay error, simplemente falta
 * un chip.
 */
const ETIQUETA_RUTA = {
  express: "Llega hoy",
  nextday: "Llega mañana",
  bodega: "Llega mañana",
  retiro: "Retiro en tienda",
};

/**
 * Pinta el resultado de cobertura DENTRO del modal.
 * Antes esto era un alert() del navegador: el momento clave del producto
 * ("llega hoy desde la tienda X") se veía como un error del sistema.
 */
function pintarResultado(cobertura, hayCobertura, tienda, eta, rutas) {
  const mensajeBackend = cobertura && cobertura.mensaje;

  let tono, titulo, texto;
  if (!hayCobertura) {
    tono = "err";
    titulo = "Todavía no llegamos ahí";
    texto = mensajeBackend || "Por ahora no tenemos una tienda que cubra esa dirección.";
  } else if (esExpress(cobertura)) {
    tono = "ok";
    titulo = "¡Sí llegamos a tu dirección!";
    texto =
      mensajeBackend ||
      (tienda
        ? `Te despacha ${tienda}, la tienda MASCA más cercana a tu zona.`
        : "Ya puedes comprar los productos disponibles en tu zona.");
  } else {
    // Llegamos, aunque no el mismo día. VA EN VERDE: es una venta, no una
    // advertencia. Este caso se pintaba en ámbar, que era coherente cuando el
    // express era la norma y el next day la versión degradada. Hoy el express
    // no opera, así que TODA compra sana caía acá y se leía como un problema.
    tono = "ok";
    titulo = "Llegamos a tu dirección";
    texto = mensajeBackend || "Hoy no alcanzamos a despachar, pero tu pedido sale mañana.";
  }

  resultadoCajaEl.dataset.tono = tono;
  resultadoTituloEl.textContent = titulo;
  resultadoTextoEl.textContent = texto;

  // Chips: ETA y rutas habilitadas. Se deduplican porque `nextday` y `bodega`
  // comparten etiqueta: si las dos están activas, "Llega mañana" saldría dos
  // veces y parecería un error de la página.
  chipsEl.innerHTML = "";
  const chips = [];
  if (hayCobertura && eta) chips.push(`Llega en ~${eta} min`);
  (rutas || []).forEach((r) => {
    const etiqueta = ETIQUETA_RUTA[r];
    if (etiqueta && chips.indexOf(etiqueta) === -1) chips.push(etiqueta);
  });
  chips.forEach((t) => {
    const chip = document.createElement("span");
    chip.className = "mloc-chip";
    chip.textContent = t;
    chipsEl.appendChild(chip);
  });

  // El botón dice qué pasa al apretarlo. "Ver productos disponibles" sonaba a
  // catálogo recortado y no comprometía nada; ahora nombra la entrega que el
  // comprador está aceptando. Sin cobertura no hay nada que ver.
  if (!hayCobertura) {
    resultadoCtaEl.textContent = "Entendido";
  } else if (esExpress(cobertura)) {
    resultadoCtaEl.textContent = "Continuar con entrega hoy";
  } else {
    resultadoCtaEl.textContent = "Continuar con entrega mañana";
  }
  setEstado("resultado");
  resultadoCtaEl.focus();
}

submitBtn.addEventListener("click", async () => {
  if (!selectedGeoData) {
    mostrarAviso("Elige una dirección de la lista o usa tu ubicación actual.", "warn");
    return;
  }

  const customer = normalizeGeoapifyCustomer(selectedGeoData);
  // La dirección "formatted" de Geoapify incluye comuna/ciudad -> mejor geocoding.
  const direccionCobertura = selectedGeoData.address || customer.address || "";

  // feedback mientras consulta
  const txtOriginal = submitBtn.textContent;
  submitBtn.disabled = true;
  submitBtn.textContent = "Verificando cobertura…";
  mostrarAviso(null);

  // 1) Cobertura MASCA. URL RELATIVA /apps/masca -> Shopify la firma (App Proxy).
  // La comuna va aparte: Geoapify ya la entrega y es la que decide la ruta de
  // bodega (el partner cubre por comuna, no por radio).
  // Van TODAS las candidatas separadas por "|", de la más específica a la más
  // general: el backend tiene la lista de comunas y elige. Ver `comunasCandidatas`.
  const comuna = (
    selectedGeoData.comunas && selectedGeoData.comunas.length > 0
      ? selectedGeoData.comunas
      : [selectedGeoData.municipality, selectedGeoData.city, customer.city].filter(Boolean)
  ).join("|");
  let cobertura = null;
  let fallaRed = false;
  try {
    const resp = await fetch(
      "/apps/masca/coverage?direccion=" +
        encodeURIComponent(direccionCobertura) +
        "&comuna=" +
        encodeURIComponent(comuna)
    );
    cobertura = await resp.json();
  } catch (err) {
    console.error("MASCA: error consultando cobertura", err);
    fallaRed = true;
  }

  submitBtn.disabled = false;
  submitBtn.textContent = txtOriginal;

  // Si ni siquiera pudimos preguntar, no inventamos una respuesta: lo decimos y
  // dejamos reintentar. Guardar "no cobertura" por un error de red sería
  // bloquear la compra de alguien que sí tiene tienda.
  if (fallaRed) {
    mostrarAviso(
      "No pudimos verificar la cobertura ahora. Revisa tu conexión e inténtalo de nuevo.",
      "err"
    );
    return;
  }

  // OJO: `disponible` significa "puede comprar ALGO", no "hay una petshop
  // abierta". Son cosas distintas: con la tienda cerrada igual se vende next day
  // y comida MASCA desde bodega. Confundirlas bloqueaba la tienda entera.
  const hayCobertura = !!(cobertura && cobertura.disponible);
  const tienda = (cobertura && cobertura.store && cobertura.store.nombre) || "";
  const eta = (cobertura && cobertura.eta_estimado_min) || null;
  // Rutas de producto habilitadas: 'express' (variante "Llega hoy"), 'nextday'
  // y/o 'bodega' ("Llega mañana"). Lo lee la Shopify Function para bloquear la
  // LÍNEA que no tiene ruta, en vez del carrito completo.
  const rutas = (cobertura && cobertura.rutas) || [];

  // 2) Guardar el estado ÚNICO y derivar de él los atributos del carrito.
  //    Un solo lugar escribe: así el header y el checkout no pueden discrepar.
  //    `confirmado_at` arranca el reloj del TTL de 12 h.
  const estado = MascaEstado.guardar({
    direccion: customer.address || direccionCobertura,
    comuna: customer.city || comuna,
    // Se guardan para que el refresco periódico de cobertura mande la misma
    // lista y no degrade a `city` (que es justo el campo equivocado).
    comunas: selectedGeoData.comunas || [],
    lat: customer.lat,
    lng: customer.lng,
    cobertura: hayCobertura ? "ok" : "no",
    rutas: rutas,
    store_nombre: tienda,
    eta_min: eta,
    mensaje: (cobertura && cobertura.mensaje) || "",
    customer: customer,
    confirmado_at: Date.now(),
  });

  // 3) Atributos del carrito = estado derivado (lo lee la Shopify Function).
  await MascaEstado.escribirCarrito(estado);

  // 4) Mostrar el resultado DENTRO del modal (antes: alert + reload inmediato).
  //    El backend manda el texto: solo él sabe si la respuesta correcta es
  //    "no llegamos", "llegamos hoy" o "la tienda cerró pero llega mañana".
  pintarResultado(cobertura, hayCobertura, tienda, eta, rutas);
});

// El reload ocurre cuando el comprador decide seguir, no de golpe.
resultadoCtaEl.addEventListener("click", () => {
  cerrarPopup();
  location.reload();
});

resultadoCambiarEl.addEventListener("click", () => {
  setEstado(selectedCoords ? "mapa" : "form");
  autocompleteInput?.focus();
});

function normalizeGeoapifyCustomer(data) {
  if (!data) return null;

  const direccion = [data.street, data.housenumber].filter(Boolean).join(" ");
  // La candidata más específica. `city` ya no manda: en el Gran Santiago dice
  // "Santiago" para direcciones que están en Ñuñoa, Providencia o Macul.
  const comuna =
    (data.comunas && data.comunas[0]) || data.city || data.municipality || "";

  return {
    // backend / firebase
    address: direccion || data.address || "",
    city: comuna,
    region: data.state || "",

    // frontend checkout
    direccion: direccion || data.address || "",
    comuna,

    lat: data.lat,
    lng: data.lng,
  };
}

/***********************
 * GATE DE COBERTURA EN "AGREGAR AL CARRITO"  (MASCA)
 * Intercepta el submit de cualquier formulario de add-to-cart de Shopify
 * (PDP, quick-add, etc.), en cualquier página:
 *   - Sin dirección elegida    -> abre el popup para pedirla.
 *   - Dirección sin cobertura  -> bloquea + avisa DENTRO del popup.
 *   - Con cobertura ("ok")     -> deja agregar al carrito NATIVO.
 * Corre en fase de CAPTURA para adelantarse al handler nativo de Dawn.
 ***********************/
document.addEventListener(
  "submit",
  function (e) {
    const form = e.target;
    if (!(form instanceof HTMLFormElement)) return;

    const action = form.getAttribute("action") || "";
    if (action.indexOf("/cart/add") === -1) return; // solo "agregar al carrito"

    // Se exige cobertura 'ok' Y VIGENTE. Una cobertura de ayer no sirve: la
    // petshop pudo haber cerrado, y el checkout la va a rechazar igual.
    // Si masca-estado.js no cargó, no bloqueamos la tienda: la reja de verdad
    // es la Shopify Function del checkout, esto es solo UX.
    if (!window.MascaEstado) return;
    const est = MascaEstado.leer();
    const vigente = MascaEstado.vigente(est);
    if (vigente && est.cobertura === "ok") return; // deja pasar al carrito nativo

    // bloquear el add-to-cart
    e.preventDefault();
    e.stopImmediatePropagation();

    // El aviso va dentro del modal, no en un alert del navegador.
    openLocationPopup();
    if (vigente && est.cobertura === "no") {
      mostrarAviso(
        "Por ahora no tenemos una tienda que llegue a tu dirección. Prueba con otra ubicación para ver los productos disponibles en tu zona.",
        "err"
      );
    } else if (est) {
      mostrarAviso("Confirma tu dirección para seguir comprando.", "warn");
    } else {
      mostrarAviso("Necesitamos tu dirección para saber qué tienda te despacha.", "warn");
    }
  },
  true
);

/*****************************************************************************
 * ARRANQUE — reconciliación y caducidad
 * ---------------------------------------------------------------------------
 * Corre en CADA carga de página y es lo que arregla el síntoma original: el
 * header mostraba una dirección (localStorage sobrevive a todo) mientras el
 * checkout pedía una (el atributo de carrito se había perdido).
 *
 *   - Cobertura VIGENTE  -> se reescribe el atributo de carrito si se perdió.
 *                           El comprador no ve nada: sigue comprando.
 *   - Cobertura VENCIDA  -> se limpian los atributos y se reabre el popup CON
 *                           LA DIRECCIÓN ANTERIOR YA CARGADA, para que sea un
 *                           clic en "Confirmar" y no volver a escribirla.
 *   - Sin dirección      -> popup en blanco, como siempre.
 *****************************************************************************/

/**
 * Deja el popup listo para confirmar la dirección anterior: llena el input,
 * arma `selectedGeoData` (que es lo que exige el botón Confirmar) y centra el
 * mapa. Devuelve true si había algo que precargar.
 */
function precargarUbicacionAnterior(est) {
  if (!est || !est.direccion) return false;

  selectedGeoData = {
    street: est.direccion,
    housenumber: "",
    city: est.comuna || "",
    municipality: est.comuna || "",
    state: "",
    address: est.direccion,
    comunas: est.comunas && est.comunas.length ? est.comunas : (est.comuna ? [est.comuna] : []),
    lat: est.lat,
    lng: est.lng,
  };

  if (autocompleteInput) autocompleteInput.value = est.direccion;
  pintarDireccionResuelta(est.direccion);
  habilitarConfirmar(true);

  // Sin coordenadas no podemos centrar el mapa: se deja que la escriba de nuevo
  // en el autocomplete, pero el texto ya está puesto.
  if (est.lat != null && est.lng != null && !isNaN(est.lat) && !isNaN(est.lng)) {
    selectedCoords = { lat: est.lat, lng: est.lng };
    try {
      initMap(est.lat, est.lng);
    } catch (e) {
      console.error("MASCA: no se pudo centrar el mapa precargado", e);
    }
  }
  return true;
}

function arrancarMasca() {
  if (typeof window.MascaEstado === "undefined") {
    console.error("MASCA: masca-estado.js no se cargó; el popup queda en modo básico");
    if (!localStorage.getItem("userAddress")) {
      setEstado("form");
      abrirPopup();
    }
    return;
  }

  const est = MascaEstado.leer();

  // Vigente: reconciliar en silencio y no molestar.
  if (MascaEstado.vigente(est)) {
    MascaEstado.reconciliar();
    return;
  }

  // Vencida o inexistente: limpiar el carrito y pedirla de nuevo, precargada.
  MascaEstado.limpiarCarrito();
  const habiaDireccion = precargarUbicacionAnterior(est);
  setEstado(habiaDireccion && selectedCoords ? "mapa" : "form");
  abrirPopup();
  if (habiaDireccion) {
    // Decir POR QUÉ se reabre: si no, parece que la tienda olvidó sus datos.
    mostrarAviso(
      "Confirma que sigues en esta dirección para ver los tiempos de entrega de hoy.",
      "warn"
    );
  }
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", arrancarMasca);
} else {
  arrancarMasca();
}
